import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Share2, BarChart3, Zap, TrendingUp, DollarSign, MousePointerClick, Target, ArrowUpRight } from "lucide-react";
import LiveSales from "@/components/LiveSales";
import PromoBanner from "@/components/PromoBanner";

const StatCard = ({ Icon, label, value, sub, accent }) => (
  <div className="p-5 rounded-2xl border border-border bg-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center justify-between mb-3">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${accent}`}><Icon size={18} className="text-white" /></div>
      {sub && <span className="text-xs font-medium text-emerald-500 flex items-center gap-0.5"><ArrowUpRight size={12} />{sub}</span>}
    </div>
    <p className="font-display text-2xl font-bold">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [recs, setRecs] = useState([]);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/analytics/recommendations").then((r) => setRecs(r.data.recommendations)).catch(() => {});
  }, []);

  const quick = [
    { Icon: Sparkles, label: "Générer une pub", to: "/studio", accent: "from-indigo-500 to-cyan-500" },
    { Icon: Share2, label: "Lancer une campagne", to: "/hub", accent: "from-cyan-500 to-emerald-500" },
    { Icon: BarChart3, label: "Voir les analytiques", to: "/analytics", accent: "from-pink-500 to-amber-500" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Bonjour, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-muted-foreground">Voici un aperçu de vos performances publicitaires.</p>
        </div>
        <Button onClick={() => navigate("/studio")} className="rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0" data-testid="dashboard-create-btn">
          <Sparkles size={16} className="mr-1.5" /> Créer une publicité
        </Button>
      </div>

      {user?.plan === "trial" && <PromoBanner />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard Icon={Zap} label="Crédits IA" value={user?.credits?.toLocaleString("fr-FR")} accent="from-amber-400 to-pink-500" />
        <StatCard Icon={DollarSign} label="CA attribué" value={`${(overview?.revenue || 0).toLocaleString("fr-FR")} €`} accent="from-emerald-400 to-cyan-500" />
        <StatCard Icon={TrendingUp} label="ROAS global" value={`${overview?.roas || 0}x`} sub={overview?.roas > 1 ? "Rentable" : null} accent="from-indigo-500 to-violet-500" />
        <StatCard Icon={Target} label="Campagnes" value={overview?.campaign_count || 0} accent="from-pink-500 to-rose-500" />
      </div>

      <LiveSales />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="p-6 rounded-2xl border border-border bg-card">
            <h3 className="font-display font-semibold text-lg mb-4">Actions rapides</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {quick.map((q) => (
                <button key={q.label} onClick={() => navigate(q.to)} className="p-4 rounded-xl border border-border hover:border-primary/40 bg-background hover:-translate-y-0.5 transition-all text-left" data-testid={`quick-${q.to.slice(1)}`}>
                  <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${q.accent} flex items-center justify-center mb-3`}><q.Icon size={18} className="text-white" /></div>
                  <p className="text-sm font-medium">{q.label}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 rounded-2xl border border-border bg-card text-center"><p className="font-display text-xl font-bold">{(overview?.impressions || 0).toLocaleString("fr-FR")}</p><p className="text-xs text-muted-foreground mt-1">Impressions</p></div>
            <div className="p-4 rounded-2xl border border-border bg-card text-center"><p className="font-display text-xl font-bold">{(overview?.clicks || 0).toLocaleString("fr-FR")}</p><p className="text-xs text-muted-foreground mt-1">Clics</p></div>
            <div className="p-4 rounded-2xl border border-border bg-card text-center"><p className="font-display text-xl font-bold">{(overview?.conversions || 0).toLocaleString("fr-FR")}</p><p className="text-xs text-muted-foreground mt-1">Conversions</p></div>
          </div>
        </div>

        <div className="p-6 rounded-2xl border border-border bg-card">
          <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2"><Sparkles size={18} className="text-primary" /> Recommandations IA</h3>
          <div className="space-y-3">
            {recs.slice(0, 4).map((r, i) => (
              <div key={i} className={`p-3 rounded-xl text-sm border ${r.severity === "success" ? "border-emerald-500/30 bg-emerald-500/5" : r.severity === "danger" ? "border-destructive/30 bg-destructive/5" : r.severity === "warning" ? "border-amber-500/30 bg-amber-500/5" : "border-border bg-background"}`} data-testid={`rec-${i}`}>
                {r.message}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
